import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteVisitor,
    blockVisitor,
    unblockVisitor,
} from '../../../redux/actions/SocietyPortal/VisitorAction/VisitorAction';
import Pagination from '../../../components/common/Pagination';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITORS_ADD,
    SOCIETY_PORTAL_VISITORS_EDIT,
    SOCIETY_PORTAL_VISITORS_VIEW,
} from '../../../Routing/routes';
import useVisitorList from './useVisitorList';
import VisitorStats from './components/VisitorStats';
import VisitorListHeader from './components/VisitorListHeader';
import VisitorFilters from './components/VisitorFilters';
import VisitorTable from './components/VisitorTable';
import VisitorCardGrid from './components/VisitorCardGrid';
import BlockVisitorModal from './components/BlockVisitorModal';
import UnblockVisitorDialog from './components/UnblockVisitorDialog';
import DeleteVisitorDialog from './components/DeleteVisitorDialog';

const Visitors = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const list = useVisitorList();
    const { deleteVisitorLoading, blockLoading } = useSelector(state => state.VisitorReducer || {});

    const [viewMode, setViewMode] = useState('list');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [blockTarget, setBlockTarget] = useState(null);
    const [unblockTarget, setUnblockTarget] = useState(null);

    const goCreate = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_ADD);
    const goView = (v) => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_VIEW.replace(':id', v.visitor_id));
    const goEdit = (v) => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_EDIT.replace(':id', v.visitor_id));

    const handleDelete = () => {
        dispatch(deleteVisitor({
            id: deleteTarget.visitor_id,
            onSuccess: () => { setDeleteTarget(null); list.refresh(); },
        }));
    };

    const handleBlock = (reason) => {
        dispatch(blockVisitor({
            id: blockTarget.visitor_id,
            reason: reason || undefined,
            onSuccess: () => { setBlockTarget(null); list.refresh(); },
        }));
    };

    const handleUnblock = () => {
        dispatch(unblockVisitor({
            id: unblockTarget.visitor_id,
            onSuccess: () => { setUnblockTarget(null); list.refresh(); },
        }));
    };

    const canCreate = can(PERMISSIONS.VMS_VISITOR_CREATE);

    // One bundle so the table and the card grid stay behaviourally identical.
    const rowActions = {
        canUpdate: can(PERMISSIONS.VMS_VISITOR_UPDATE),
        canDelete: can(PERMISSIONS.VMS_VISITOR_DELETE),
        busy: blockLoading,
        onView: goView,
        onEdit: goEdit,
        onBlock: setBlockTarget,
        onUnblock: setUnblockTarget,
        onDelete: setDeleteTarget,
    };

    const emptyState = {
        filtered: list.hasFilters,
        canCreate,
        onClear: list.clearFilters,
        onCreate: goCreate,
    };

    const ListBody = viewMode === 'list' ? VisitorTable : VisitorCardGrid;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <VisitorStats
                summary={list.summary}
                loading={list.summaryLoading}
                showBlockedCallout={list.blockedFilter !== 'true'}
                onShowBlocked={() => list.setBlockedFilter('true')}
            />

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <VisitorListHeader
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    canCreate={canCreate}
                    onCreate={goCreate}
                />

                <VisitorFilters
                    search={list.search}
                    onSearchChange={list.setSearch}
                    visitorType={list.visitorType}
                    onVisitorTypeChange={list.setVisitorType}
                    blockedFilter={list.blockedFilter}
                    onBlockedFilterChange={list.setBlockedFilter}
                    hasFilters={list.hasFilters}
                    onClear={list.clearFilters}
                />

                <ListBody
                    rows={list.rows}
                    loading={list.loading}
                    actions={rowActions}
                    empty={emptyState}
                />

                {!list.loading && list.rows.length > 0 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination
                            currentPage={list.page}
                            totalPages={list.totalPages}
                            totalItems={list.totalItems}
                            itemsPerPage={list.itemsPerPage}
                            onPageChange={list.setPage}
                            onItemsPerPageChange={list.setItemsPerPage}
                            itemsPerPageOptions={[10, 20, 50, 100]}
                            itemLabel="visitors"
                        />
                    </div>
                )}
            </div>

            <BlockVisitorModal
                visitor={blockTarget}
                submitting={blockLoading}
                onCancel={() => setBlockTarget(null)}
                onConfirm={handleBlock}
            />

            <UnblockVisitorDialog
                visitor={unblockTarget}
                busy={blockLoading}
                onCancel={() => setUnblockTarget(null)}
                onConfirm={handleUnblock}
            />

            <DeleteVisitorDialog
                visitor={deleteTarget}
                busy={deleteVisitorLoading}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default Visitors;
